import { OLSwitch } from "components/FormControls/Toggle";
import { ReactComponent as DragIndicatorIcon } from "components/Icons/drag-indicator.svg";
import type { Identifier, XYCoord } from "dnd-core";
import { memo, useEffect, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { useStyles } from "./ColumnManager";

interface Props {
  label: string;
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
  index: number;
  updateTable: () => void;
  checked: boolean;
  toggleVisibility: (isVisible: boolean, index: number) => void;
  disableLastColumnSwitch: boolean;
  "data-test-id": string;
}
interface DragItem {
  index: number;
  label: string;
  type: string;
}

export const Column = memo(
  ({
    label,
    moveColumn,
    index,
    updateTable,
    checked,
    toggleVisibility,
    disableLastColumnSwitch,
    "data-test-id": dataTestId,
  }: Props) => {
    const classes = useStyles({});
    const ref = useRef<HTMLDivElement>(null);
    const [{ handlerId }, drop] = useDrop<
      DragItem,
      void,
      { handlerId: Identifier | null }
    >({
      accept: "item",
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
        };
      },
      hover(item: DragItem, monitor) {
        if (!ref.current) {
          return;
        }
        const dragIndex = item.index;
        const hoverIndex = index;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return;
        }
        // Determine rectangle on screen
        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        const clientOffset = monitor.getClientOffset();
        const hoverClientY =
          (clientOffset as XYCoord).y - hoverBoundingRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }
        moveColumn(dragIndex, hoverIndex);

        item.index = hoverIndex;
      },
    });

    const [{ isDragging, didDrop }, drag] = useDrag({
      type: "item",
      item: () => {
        return { label, index };
      },
      collect: (monitor: any) => {
        return { isDragging: monitor.isDragging(), didDrop: monitor.didDrop() };
      },
    });

    useEffect(() => {
      if (didDrop) {
        updateTable();
      }
      // eslint-disable-next-line
    }, [didDrop]);

    const opacity = isDragging ? 0 : 1;
    drag(drop(ref));

    return (
      <div
        ref={ref}
        style={{ opacity }}
        data-handler-id={handlerId}
        className={classes.item}
        data-test-id={`${dataTestId}-item`}
      >
        <div className={classes.itemName}>
          <DragIndicatorIcon />
          <span className={classes.label}>{label}</span>
        </div>
        <OLSwitch
          data-test-id={`${dataTestId}-switch`}
          checked={checked}
          disabled={checked && disableLastColumnSwitch}
          onChange={(e) => {
            toggleVisibility(e.target.checked, index);
          }}
        />
      </div>
    );
  },
);
